/**
 * Global Data-Scope drawer (SYR-68).
 *
 * Right-hand drawer opened by the top-bar scope pill. Fields: Period, Customer
 * Cohort, Countries — the marketer-view-wide data window. Brand is NOT here
 * (it lives in the top-bar brand chip + the URL). Draft state is local; only
 * "Calculate & View Results" commits to the shared MarketerScope (and thus
 * re-renders every destination). Period options mirror the platform's
 * PeriodEnum presets ("Last N Days" + All Time).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Drawer, Select, Button, Typography, Grid } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuth } from '../../../hooks/useAuth';
import { useGetSurveysQuery } from '../../../services/nestjs-base-api';
import { COUNTRY_OPTIONS } from '../../../constants/countries';
import {
  useMarketerScope,
  type MarketerScope,
  type PeriodType,
} from '../scope/MarketerScopeContext';
import { THEME } from '../config/theme';

const { Text } = Typography;

const PERIOD_OPTIONS: { value: PeriodType; labelId: string; label: string }[] = [
  { value: 'last30', labelId: 'marketerView.period.last30', label: 'Last 30 Days' },
  { value: 'last60', labelId: 'marketerView.period.last60', label: 'Last 60 Days' },
  { value: 'last90', labelId: 'marketerView.period.last90', label: 'Last 90 Days' },
  { value: 'last120', labelId: 'marketerView.period.last120', label: 'Last 120 Days' },
  { value: 'last180', labelId: 'marketerView.period.last180', label: 'Last 180 Days' },
  { value: 'last365', labelId: 'marketerView.period.last365', label: 'Last 365 Days' },
  { value: 'allTime', labelId: 'marketerView.period.allTime', label: 'All Time' },
];

interface ScopeDrawerProps {
  open: boolean;
  onClose: () => void;
}

const fieldLabelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: 8,
  fontWeight: 700,
  fontSize: 11,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: THEME.textSecondary,
};

export const ScopeDrawer: React.FunctionComponent<ScopeDrawerProps> = ({
  open,
  onClose,
}) => {
  const intl = useIntl();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const { user } = useAuth();
  const { activeBrand, scope, setScope, resetScope, cohorts } =
    useMarketerScope();

  // Local draft — committed only on "Calculate & View Results".
  const [draft, setDraft] = useState<MarketerScope>(scope);
  useEffect(() => {
    if (open) setDraft(scope);
  }, [open, scope]);

  const { data: surveys } = useGetSurveysQuery(
    {
      jwtToken: user?.jwtToken ?? '',
      organisationId: user?.organisation?.id ?? 0,
    },
    { skip: !user?.jwtToken || !user?.organisation?.id },
  );

  const availableCountries = useMemo(() => {
    if (!activeBrand?.id || !surveys) return [];
    const brandSurveys = surveys.filter((s) => {
      const surveyBrandId =
        (s as { brandId?: number }).brandId ??
        (s as { brand?: { id?: number } }).brand?.id;
      return surveyBrandId === activeBrand.id;
    });
    const codes = Array.from(
      new Set(brandSurveys.map((s) => s.countryCode).filter(Boolean)),
    );
    return COUNTRY_OPTIONS.filter((c) => codes.includes(c.code));
  }, [activeBrand?.id, surveys]);

  const handleApply = (): void => {
    setScope(draft);
    onClose();
  };

  const handleClear = (): void => {
    resetScope();
    setDraft({ periodType: 'last180', cohortIds: [], countryCodes: [] });
  };

  return (
    <Drawer
      title={
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: THEME.textBase }}>
            <FormattedMessage id="marketerView.scope.title" defaultMessage="Data Selection" />
          </div>
          <div style={{ fontSize: 13, fontWeight: 400, color: THEME.textSecondary, marginTop: 2 }}>
            <FormattedMessage
              id="marketerView.scope.subtitle"
              defaultMessage="Select the data to analyse, applies to every page"
            />
          </div>
        </div>
      }
      rootClassName="mv-scope-drawer"
      closable={false}
      extra={
        <Button type="text" icon={<CloseOutlined />} onClick={onClose} aria-label="Close" />
      }
      placement="right"
      width={isMobile ? '100%' : 404}
      open={open}
      onClose={onClose}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <Button onClick={handleClear}>
            <FormattedMessage
              id="marketerView.scope.clear"
              defaultMessage="Clear Filters"
            />
          </Button>
          <Button type="primary" onClick={handleApply}>
            <FormattedMessage
              id="marketerView.scope.apply"
              defaultMessage="Calculate & View Results"
            />
          </Button>
        </div>
      }
    >
      {/*
        A global rule sets `.ant-drawer-body { display:flex; text-align:center }`
        which shrinks + centers our fields (so width:100% selects looked narrow).
        Override it to a normal left-aligned block. Also: multiple selects show a
        text caret by default — make the whole control read as clickable (pointer)
        like the hand-off.
      */}
      <style>{`
        .mv-scope-drawer .ant-drawer-body {
          display: block !important;
          text-align: left !important;
          margin: 0 !important;
          width: 100% !important;
        }
        .mv-scope-drawer .ant-select-selector,
        .mv-scope-drawer .ant-select-selector * { cursor: pointer !important; }
      `}</style>
      <div style={{ marginBottom: 24 }}>
        <Text style={fieldLabelStyle}>
          <FormattedMessage
            id="marketerView.scope.countries"
            defaultMessage="Countries"
          />
        </Text>
        <Select<string[]>
          mode="multiple"
          allowClear
          size="large"
          style={{ width: '100%' }}
          placeholder={intl.formatMessage({
            id: 'marketerView.scope.countriesPlaceholder',
            defaultMessage: 'All countries',
          })}
          value={draft.countryCodes}
          onChange={(countryCodes) => setDraft((d) => ({ ...d, countryCodes }))}
          options={availableCountries.map((c) => ({
            value: c.code,
            label: c.name,
          }))}
        />
      </div>

      <div style={{ marginBottom: 24 }}>
        <Text style={fieldLabelStyle}>
          <FormattedMessage
            id="marketerView.scope.cohort"
            defaultMessage="Customer Cohort"
          />
        </Text>
        <Select<number[]>
          mode="multiple"
          allowClear
          size="large"
          style={{ width: '100%' }}
          placeholder={intl.formatMessage({
            id: 'marketerView.scope.cohortPlaceholder',
            defaultMessage: 'All cohorts',
          })}
          value={draft.cohortIds}
          onChange={(cohortIds) => setDraft((d) => ({ ...d, cohortIds }))}
          options={cohorts.map((c) => ({ value: c.id, label: c.name }))}
        />
      </div>

      <div>
        <Text style={fieldLabelStyle}>
          <FormattedMessage
            id="marketerView.scope.period"
            defaultMessage="Period"
          />
        </Text>
        <Select<PeriodType>
          size="large"
          style={{ width: '100%' }}
          value={draft.periodType === 'custom' ? 'last180' : draft.periodType}
          onChange={(periodType) => setDraft((d) => ({ ...d, periodType }))}
          options={PERIOD_OPTIONS.map((o) => ({
            value: o.value,
            label: intl.formatMessage({ id: o.labelId, defaultMessage: o.label }),
          }))}
        />
      </div>
    </Drawer>
  );
};
